import { ChatContext, ChatPageType } from './pageContext';

// Shaped exactly as a future GET response body: pure JSON, no functions, no JSX.
export type SamplePrompt = {
  id: string;
  label: string;
  prompt: string;
  contexts: ChatPageType[];
};

const SAMPLE_PROMPTS: SamplePrompt[] = [
  {
    id: 'campaign-not-delivering',
    label: "Why isn't this delivering?",
    prompt: 'Why is this campaign not delivering? Walk through its delivery profile and anything blocking impressions.',
    contexts: ['campaign'],
  },
  {
    id: 'campaign-changed-this-week',
    label: 'What changed here this week?',
    prompt: 'What changed on this campaign in the last 7 days, and who made each change?',
    contexts: ['campaign'],
  },
  {
    id: 'campaign-geo-targeting',
    label: 'Check the geo targeting',
    prompt: 'Which states and locations is this campaign targeting, and does that match the locations on its IO?',
    contexts: ['campaign'],
  },
  {
    id: 'campaign-readiness',
    label: 'Is it ready to run?',
    prompt: 'Is this campaign ready to run? List every requirement that is not met and what it would take to fix each one.',
    contexts: ['campaign'],
  },
  {
    id: 'io-locations-right-states',
    label: 'Are locations targeting the right states?',
    prompt:
      'Are all locations on this IO targeting the right states? Flag any location whose state does not match its targeting.',
    contexts: ['insertion_order'],
  },
  {
    id: 'io-impression-budget-owner',
    label: 'Who set this impression budget?',
    prompt: 'Who set the impression budget on this IO, when did they set it, and what was it before?',
    contexts: ['insertion_order'],
  },
  {
    id: 'io-pacing',
    label: 'How is it pacing?',
    prompt: 'How is this IO pacing against its impression budget, and which of its campaigns are behind?',
    contexts: ['insertion_order'],
  },
  {
    id: 'io-open-alerts',
    label: 'What alerts are open?',
    prompt: 'What alerts are open on this IO right now, and which one should I deal with first?',
    contexts: ['insertion_order'],
  },
  {
    id: 'contract-triage-alerts',
    label: 'Triage the open alerts',
    prompt: 'Triage the open alerts on this contract: group them by severity and tell me what to fix first.',
    contexts: ['contract'],
  },
  {
    id: 'contract-states-by-io',
    label: 'Show targeted states by IO',
    prompt: 'Show the targeted states for each IO on this contract, and call out any IO that looks inconsistent.',
    contexts: ['contract'],
  },
  {
    id: 'contract-underdelivery-risk',
    label: "What's at risk of underdelivering?",
    prompt: 'Which campaigns on this contract are at risk of underdelivering, and what is driving the risk?',
    contexts: ['contract'],
  },
  {
    id: 'contract-changed-this-week',
    label: 'What changed this week?',
    prompt: 'What changed on this contract in the last 7 days, and who made each change?',
    contexts: ['contract'],
  },
  {
    id: 'board-alert-rollup',
    label: "Summarize today's alerts",
    prompt: 'Summarize the open alerts across contracts: how many by severity, and which contracts are worst off.',
    contexts: ['board'],
  },
  {
    id: 'board-worst-contracts',
    label: 'Which contracts need attention?',
    prompt: 'Which contracts have the most urgent open alerts right now, and what is wrong with each?',
    contexts: ['board'],
  },
  {
    id: 'agent-capabilities',
    label: 'What can you do?',
    prompt:
      'What can you help me with? List the kinds of questions you can answer about contracts, IOs, campaigns, locations, delivery, and alerts.',
    contexts: ['board', 'generic'],
  },
];

// The one seam a later release replaces: samples served from the ACH backend, merged
// with the user's saved favorites. Callers use this, never the const above.
export const getSamplePrompts = (context: ChatContext): SamplePrompt[] =>
  SAMPLE_PROMPTS.filter((sample) => sample.contexts.includes(context.pageType));

export default getSamplePrompts;
